/*
This screen is visible only when the user is redirected from the AC NOC Dashboard
(for a live session or a failed transaction).
Otherwise, the user cannot access this screen.
*/

/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLoader, downloadFile, FilterProp, formatRelativeTimeForTable, renderHeaderActionButtons, TableDashboard, useAppDispatch, useAppSelector, useEffectOnce } from '../../../globalUtils/globalExports';
import {
  acReportTypes,
  exportAcStatusHistoryReport,
  fetchAcStatusHistoryReportData,
  getAcFilterReducer,
  getAcReferenceDataReducer,
  getAcreportsReducer,
  setAcRedirectedStateForStatusHistoryFilters,
} from '../../rmsReduxStore/acReduxExports';
import { setAcReportListTableProps } from '../../rmsReduxStore/acreportsRedux/acReportCreator';
import { fetchAcChargerVisibleIdReferenceData } from '../../rmsReduxStore/acReferenceDataRedux/acReferenceDataCreator';
import { getCurrentAcFiltersValues, getAcFilterSelectedValue, setAcFilterData } from '../../rmsReduxStore/acFiltersRedux/acFiltersCreators';
import access from '../../../auth/service/AccessControl';
import { cloneDeep } from 'lodash';
import { Id, toast } from 'react-toastify';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { NoDataComponentStatusHistory } from '../../../globalUtils/TableDashboard/TableComponents';
import { useNavigate } from 'react-router-dom';


const AcStatusHistoryDashboard: React.FC = () => {

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { statusHistoryColumnHeaders, statusHistoryData, statusHistoryCount, reportLoader, statusHistoryListTableProps, statusHistoryFiltersAppliedFromRedirection } = useAppSelector(getAcreportsReducer);
  const { chargerVisibleIdReferenceData } = useAppSelector(getAcReferenceDataReducer);
  const { screenFilters } = useAppSelector(getAcFilterReducer);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exportingFile, setExportingFile] = useState(false);
  const [dashboardLoader, setDashboardLoader] = useState(false);
  const statusHistoryListTablePropsRef = useRef(statusHistoryListTableProps);
  const isOrgTypeCustomer = access?.isOrgTypeCustomer();
  const params = new URLSearchParams(location?.search);
  const sessionType = params?.get('sessionType') ?? 'failed';
  const redirectedFrom = params?.get('redirectedFrom');

  useEffectOnce(() => {
    if (!redirectedFrom || redirectedFrom != 'nocDashboard') navigate('/ac/dashboard');
  });

  useEffect(() => {
    statusHistoryListTablePropsRef.current = statusHistoryListTableProps;
  }, [statusHistoryListTableProps]);

  const onChangeOfSearchText = useCallback(async (searchTerm) => {
    if (statusHistoryListTablePropsRef) {
      await layoutCallback(1, statusHistoryListTablePropsRef?.current?.pageSize, statusHistoryListTablePropsRef?.current?.view, statusHistoryListTablePropsRef?.current?.sortBy, statusHistoryListTablePropsRef?.current?.order, null, searchTerm);
    }
  }, [statusHistoryListTablePropsRef, statusHistoryColumnHeaders, statusHistoryData]);

  const searchBoxConfiguration = useMemo(() => {
    return {
      searchFieldId: 'manage-ac-status-history-search-box',
      searchFieldName: 'manage-ac-status-history-search-box',
      debounceTime: 600,
      handleSearch: onChangeOfSearchText,
    };
  }, [onChangeOfSearchText]);

  const headerActionBtns = renderHeaderActionButtons({
    actionBtns: [
      {
        id: 'export_csv_btn',
        isAccessible: isOrgTypeCustomer ? true : access?.reports?.chargingHistory?.export(),
        headerActionBtnClick: (event): void => {
          event?.stopPropagation();
          setExportAnchorEl(event.currentTarget);
        }
      }
    ]
  });

  const mobileViewConfiguration = useMemo(() => {
    return {
      headerDataConfig: {
        headerLeftDataConfig: ['name', 'type'],
        headerRightDataConfig: {
          actionIconsComponent: false,
          statusIconComponent: true,
        },
      },
      statusIconKey: 'status',
      contentDataConfig: [[]],
    };
  }, []);

  const getTableRows = useCallback(() => {
    if (!statusHistoryData || !statusHistoryColumnHeaders) {
      return [];
    }
    const data = cloneDeep(statusHistoryData);
    statusHistoryColumnHeaders?.forEach((column) => {
      const { key, dataType, unit } = column;
      if (key && (dataType || unit)) {
        data?.forEach((historyItem) => {
          if (historyItem?.[key] && dataType === 'Date' && historyItem[key] !== 'NA') {
            historyItem[key] = formatRelativeTimeForTable(historyItem[key]);
          }
          if (historyItem?.[key] !== null && historyItem?.[key] !== undefined && unit && historyItem?.[key] !== 'NA') {
            historyItem[key] = `${historyItem[key]} ${unit}`;
          }
        });
      }
    });
    return data || [];
  }, [statusHistoryData, statusHistoryColumnHeaders]);

  const statusHistoryColumns = statusHistoryColumnHeaders?.map(item => {
    return ({ field: item?.key, description: item?.description, flex: 1, headerName: item?.visibleName, minWidth: (item?.visibleName?.length > 25) ? 200 : 150, pinnable: false });
  });

  const layoutCallback = useCallback(async (pageNumber: number, pageSize: number, view: string, sortField: string, sortOrder: string, filterValue, searchTerm) => {
    const tableProps: IListTableProps = cloneDeep(statusHistoryListTablePropsRef.current);
    await dispatch(fetchAcStatusHistoryReportData({
      sortBy: sortField || '',
      order: sortOrder || 'ascending',
      pageSize,
      pageNumber,
      filters: filterValue ? filterValue : getCurrentAcFiltersValues('acStatusHistoryReport', true),
      searchTerm: searchTerm != null && searchTerm != undefined ? searchTerm : statusHistoryListTablePropsRef?.current?.searchTerm,
      sessionType: sessionType
    }));
    if (tableProps) {
      tableProps.view = view || 'list';
      tableProps.sortBy = sortField || '';
      tableProps.order = sortOrder || 'ascending';
      tableProps.pageNumber = pageNumber;
      tableProps.pageSize = pageSize;
      if (searchTerm != null && searchTerm != undefined) {
        tableProps.searchTerm = searchTerm;
      }
    }
    await dispatch(setAcReportListTableProps(tableProps, 'statusHistory'));
  }, [statusHistoryListTablePropsRef, dispatch]);

  const fileDownloadSuccessToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.success('File downloaded.');
  }, []);

  const fileDownloadFailureToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.warn('Something went wrong. Please export again.');
  }, []);

  const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
    if (exportingFile) return;
    try {
      setDashboardLoader(true);
      const response = await dispatch(exportAcStatusHistoryReport({
        fileType: fileType,
        filters: getCurrentAcFiltersValues('acStatusHistoryReport', true),
        sessionType: sessionType
      }));
      if (response?.status === 202 || response?.status === 200) {
        setDashboardLoader(false);
        downloadFile({ url: response?.data?.url });
        fileDownloadSuccessToast();
      } else {
        setDashboardLoader(false);
        fileDownloadFailureToast();
        setExportAnchorEl(null);
      }
    } finally {
      setDashboardLoader(false);
      setExportingFile(false);
      setExportAnchorEl(null);
    }
  }, [exportingFile, fileDownloadSuccessToast, fileDownloadFailureToast]);

  const handleClose = (): void => {
    setExportAnchorEl(null);
  };

  const renderExportMenuItem = (fileType: 'csv' | 'xlsx', label: string): JSX.Element => (
    <MenuItem key={fileType}>
      <div className='export__btn-option' onClick={(e) => {
        e?.stopPropagation();
        onExportBtnClick(fileType);
      }}>
        {label}
      </div>
    </MenuItem>
  );

  const handleFilterDataChange = useCallback(async (val, filterKey, isGlobal) => {
    if (isGlobal && filterKey === 'customer') {
      const reqBody = {
        customerIds: val?.filter((item) => item?.id === 'all')?.length > 0 ? ['all'] : val?.map((customer) => customer?.id)
      };
      dispatch(fetchAcChargerVisibleIdReferenceData(reqBody));
    }
    const res = await setAcFilterData(val, filterKey, 'acStatusHistoryReport', 'SET', isGlobal ? isGlobal : false, true)(dispatch);
    if (res?.message === 'Action dispatched successfully') {
      if (statusHistoryListTablePropsRef?.current) {
        await layoutCallback(1, statusHistoryListTablePropsRef?.current?.pageSize, statusHistoryListTablePropsRef?.current?.view, statusHistoryListTablePropsRef?.current?.sortBy, statusHistoryListTablePropsRef?.current?.order, res?.filters, statusHistoryListTablePropsRef?.current?.searchTerm);
      }
    }
  }, [statusHistoryData, dispatch]);

  const statusHistoryFilterProps: FilterProp[] = useMemo(() => {
    return [
      {
        filterLabel: 'Chargers',
        filterType: 'dropdown',
        filterId: 'chargerId',
        filterDropdownProps: {
          selectValue: getAcFilterSelectedValue('chargerId', 'acStatusHistoryReport', false),
          selectOptions: chargerVisibleIdReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'chargerId', false);
          },
          selectDropDownId: 'ac-status-history-chargerId-filter-dropdown',
          isMultiSelect: true
        },
      }
    ];
  }, [screenFilters]);

  const onResetButtonClick = useCallback(async (callList: boolean) => {
    const res = await setAcFilterData(null, '', 'acStatusHistoryReport', 'CLEAR', false)(dispatch);
    if (res?.message === 'Action dispatched successfully') {
      if (callList && statusHistoryListTablePropsRef?.current) {
        const tableProps = cloneDeep(statusHistoryListTablePropsRef?.current);
        await layoutCallback(1, tableProps?.pageSize, tableProps?.view, tableProps?.sortBy, tableProps?.order, res?.filters, tableProps?.searchTerm ?? '');
      }
    }
  }, [statusHistoryListTablePropsRef?.current]);

  const statusHistoryCleanUpStates = useCallback(() => {
    const action = {
      type: acReportTypes.CLEAR_AC_STATUS_HISTORY_LIST_DATA,
    };
    dispatch(action);
    if (statusHistoryFiltersAppliedFromRedirection) {
      onResetButtonClick(true);
    }
    setAcRedirectedStateForStatusHistoryFilters(false)(dispatch);
  }, [statusHistoryFiltersAppliedFromRedirection]);

  useEffect(() => {
    window.addEventListener('beforeunload', statusHistoryCleanUpStates);
    return (): void => {
      statusHistoryCleanUpStates();
      window.removeEventListener('beforeunload', statusHistoryCleanUpStates);
    };
  }, []);

  return (
    <>
      <DashboardLoader showLoader={dashboardLoader} />
      <TableDashboard
        key="manage-ac-status-history-table"
        tableId="manage-ac-status-history-table"
        tableClassName="manage__status__history__table"
        header={'Charging Sessions'}
        searchBoxIncluded={true}
        searchBoxConfiguration={searchBoxConfiguration}
        gridColumns={statusHistoryColumns}
        tableRows={getTableRows()}
        mobileViewConfiguration={mobileViewConfiguration}
        layoutView={'list'}
        headerActionBtns={headerActionBtns}
        listLayoutCallBack={layoutCallback}
        totalCount={statusHistoryCount}
        showSkeleton={!statusHistoryData}
        totalCountText={'charging sessions'}
        showLoader={reportLoader}
        customNoDataComponent={NoDataComponentStatusHistory}
        pageNumber={statusHistoryListTableProps?.pageNumber}
        tablePageSize={statusHistoryListTableProps?.pageSize}
        filterProps={statusHistoryFilterProps}
        handleFilterDataChange={handleFilterDataChange}
        referenceDataCallbacksFilters={['acChargerVisibleId']}
        leftPinnedColumns={['visibleId', 'connectorNo']}
        excludeGlobalFilters={true}
        screen='ac_status_history'
      />
      <Menu
        id="export-option-dropdown"
        anchorEl={exportAnchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        className='export__option__dropdown'
        sx={{ top: '30px' }}
        open={Boolean(exportAnchorEl)}
        onClose={handleClose}
      >
        {renderExportMenuItem('csv', 'To CSV')}
        {renderExportMenuItem('xlsx', 'To Excel')}
      </Menu>
    </>
  );
};


export default AcStatusHistoryDashboard;
